import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format, addDays, addWeeks, addMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AppointmentWithDetails, Veterinarian } from "@/hooks/useAppointments";
import type { AppointmentStatus } from "@/hooks/useAppointmentStatuses";
import { useMobileAgendaEvents } from "./useMobileAgendaEvents";
import { eventsForDay } from "@/lib/calendar/mobile-agenda";
import { MobileMonthView } from "./MobileMonthView";
import { MobileWeekView } from "./MobileWeekView";
import { MobileDayView } from "./MobileDayView";
import { MobileDayEventList } from "./MobileDayEventList";
import { MobileCalendarFilterChips } from "./MobileCalendarFilterChips";
import { MobileCalendarFilterSheet, type FilterOption } from "./MobileCalendarFilterSheet";
import { useMobileHeader, useMobileHeaderContext } from "@/contexts/MobileHeaderContext";

type ViewMode = "day" | "week" | "month";

type Props = {
  className?: string;
  appointments: AppointmentWithDetails[];
  professionals: Veterinarian[];
  statuses: AppointmentStatus[];
  viewMode: ViewMode;
  setViewMode: (v: ViewMode) => void;
  currentDate: Date;
  onNavigate: (date: Date) => void;
  onAppointmentClick: (id: string) => void;
  onCreateAppointment: () => void;
  searchTerm: string; setSearchTerm: (v: string) => void;
  vetOptions: FilterOption[]; selectedVets: string[]; setSelectedVets: (v: string[]) => void;
  typeOptions: FilterOption[]; selectedTypes: string[]; setSelectedTypes: (v: string[]) => void;
  statusOptions: FilterOption[]; selectedStatuses: string[]; setSelectedStatuses: (v: string[]) => void;
  onlyTaxiDog: boolean; setOnlyTaxiDog: (v: boolean) => void;
};

const SEGMENTS: ViewMode[] = ["day", "week", "month"];

const sentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export function MobileCalendar(props: Props) {
  const { t } = useTranslation("calendar");
  const { scrolled } = useMobileHeaderContext();
  const { grouped } = useMobileAgendaEvents(props.appointments, props.professionals);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const today = new Date();

  const dayEvents = eventsForDay(props.currentDate, grouped);
  const activeCount =
    props.selectedVets.length + props.selectedTypes.length +
    props.selectedStatuses.length + (props.onlyTaxiDog ? 1 : 0) + (props.searchTerm ? 1 : 0);

  const periodTitle = sentenceCase(
    props.viewMode === "month" ? format(props.currentDate, "MMMM yyyy", { locale: ptBR })
    : props.viewMode === "week" ? format(props.currentDate, "'Semana de' d 'de' MMMM yyyy", { locale: ptBR })
    : format(props.currentDate, "EEEE, d MMM", { locale: ptBR })
  );

  useMobileHeader({
    title: t("title"),
    search: (
      <MobileCalendarFilterChips
        activeCount={activeCount}
        searchTerm={props.searchTerm}
        onOpen={() => setFiltersOpen(true)}
      />
    ),
  }, [t, activeCount, props.searchTerm]);

  const step = (dir: 1 | -1) => {
    const fn = props.viewMode === "month" ? addMonths : props.viewMode === "week" ? addWeeks : addDays;
    props.onNavigate(fn(props.currentDate, dir));
  };

  const clearFilters = () => {
    props.setSearchTerm("");
    props.setSelectedVets([]);
    props.setSelectedTypes([]);
    props.setSelectedStatuses([]);
    props.setOnlyTaxiDog(false);
  };

  return (
    <div className={cn("space-y-4", props.viewMode === "day" && "pb-28", props.className)}>
      <div
        className="flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => props.onNavigate(new Date())}
            className="h-8 rounded-full border border-border bg-card px-3 text-[12px] font-semibold text-foreground"
          >
            {t("mobile.today")}
          </button>
          <button
            type="button"
            aria-label={t("mobile.previous")}
            onClick={() => step(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={t("mobile.next")}
            onClick={() => step(1)}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex gap-0.5 rounded-full border border-border bg-card p-0.5">
          {SEGMENTS.map((seg) => (
            <button
              key={seg}
              type="button"
              aria-pressed={props.viewMode === seg}
              onClick={() => props.setViewMode(seg)}
              className={cn(
                "h-8 rounded-full px-3 text-[12px] font-semibold transition-colors",
                props.viewMode === seg ? "bg-primary text-primary-foreground" : "text-muted-foreground",
              )}
            >
              {t(`mobile.view.${seg}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="text-lg font-bold text-foreground">{periodTitle}</div>

      {props.viewMode === "month" ? (
        <div className="space-y-4">
          <MobileMonthView selected={props.currentDate} today={today} grouped={grouped} onSelectDay={props.onNavigate} />
          <MobileDayEventList date={props.currentDate} events={dayEvents} statuses={props.statuses} onAppointmentClick={props.onAppointmentClick} />
        </div>
      ) : props.viewMode === "week" ? (
        <div className="space-y-4">
          <MobileWeekView selected={props.currentDate} grouped={grouped} onSelectDay={props.onNavigate} />
          <MobileDayEventList date={props.currentDate} events={dayEvents} statuses={props.statuses} onAppointmentClick={props.onAppointmentClick} />
        </div>
      ) : (
        <MobileDayView
          date={props.currentDate}
          grouped={grouped}
          onAppointmentClick={props.onAppointmentClick}
        />
      )}

      <button
        type="button"
        aria-label={t("newAppointment")}
        onClick={props.onCreateAppointment}
        className="fixed bottom-[calc(96px+env(safe-area-inset-bottom))] right-4 z-40 inline-flex h-12 items-center gap-2 rounded-full bg-primary px-5 text-[15px] font-bold text-primary-foreground shadow-xl shadow-primary/25 md:hidden"
      >
        <Plus className="h-[18px] w-[18px]" />
        {t("mobile.newShort")}
      </button>

      <MobileCalendarFilterSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchTerm={props.searchTerm} setSearchTerm={props.setSearchTerm}
        vetOptions={props.vetOptions} selectedVets={props.selectedVets} setSelectedVets={props.setSelectedVets}
        typeOptions={props.typeOptions} selectedTypes={props.selectedTypes} setSelectedTypes={props.setSelectedTypes}
        statusOptions={props.statusOptions} selectedStatuses={props.selectedStatuses} setSelectedStatuses={props.setSelectedStatuses}
        onlyTaxiDog={props.onlyTaxiDog} setOnlyTaxiDog={props.setOnlyTaxiDog}
        onClear={clearFilters}
      />
    </div>
  );
}
